import React, { useState } from 'react';
import axios from 'axios';
import GetAppIcon from '@mui/icons-material/GetApp';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DCR_API_BASE_SERVER_URL } from 'config';

interface Attachment {
  fileName: string;
  mimeType: string;
}

interface AttachmentsFieldProps {
  attachments: Attachment[];
  agentActivityLogId: string;
}

const fetchAttachmentBlob = async (agentActivityLogId: string, fileName: string): Promise<string> => {
  const url = `${DCR_API_BASE_SERVER_URL}/assistant-activity-log/attachments/download?agentActivityLogId=${agentActivityLogId}&fileName=${encodeURIComponent(fileName)}`;
  const response = await axios.get(url, { responseType: 'blob' });
  return window.URL.createObjectURL(response.data);
};

export const AttachmentsField = ({ attachments, agentActivityLogId }: AttachmentsFieldProps) => {
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const handleDownload = async (fileName: string) => {
    setLoadingKey(`download:${fileName}`);
    try {
      const blobUrl = await fetchAttachmentBlob(agentActivityLogId, fileName);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } finally {
      setLoadingKey(null);
    }
  };

  const handleView = async (fileName: string) => {
    setLoadingKey(`view:${fileName}`);
    try {
      const blobUrl = await fetchAttachmentBlob(agentActivityLogId, fileName);
      window.open(blobUrl, '_blank');
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <Stack spacing={0.75}>
      {attachments.map(({ fileName }) => (
        <Stack
          key={fileName}
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}
        >
          <Tooltip title="Download">
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={loadingKey !== null}
                onClick={() => handleDownload(fileName)}
              >
                {loadingKey === `download:${fileName}` ? (
                  <CircularProgress size={14} />
                ) : (
                  <GetAppIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="View">
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={loadingKey !== null}
                onClick={() => handleView(fileName)}
              >
                {loadingKey === `view:${fileName}` ? (
                  <CircularProgress size={14} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <InsertDriveFileIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />

          <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
            {fileName}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};
